import React, { useState } from 'react';

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';

import { createDefaultSpendPointsTile } from '~/utils/societePayloadBuilder/spendPoints/defaults';
import { formatTileTitle, getNextTileDraftLabel, moveTile } from '~/utils/societePayloadBuilder/spendPoints/tiles';
import type { SpendPointsWaysToBoostTile } from '~/utils/societePayloadBuilder/spendPoints/types';

import {
  PAYLOAD_BUILDER_DESCRIPTION_WARNING_LENGTH,
  PAYLOAD_BUILDER_TITLE_WARNING_LENGTH,
  PayloadBuilderImageIdField,
  PayloadBuilderTextField,
  getPayloadBuilderCopyLengthWarningProps,
} from '../FormFields';
import PayloadBuilderRegionChipsField from '../RegionChipsField';
import { SectionCard } from '../SectionCard';

interface SpendPointsTilesSectionProps {
  tiles: Array<SpendPointsWaysToBoostTile>;
  onChange: (tiles: Array<SpendPointsWaysToBoostTile>) => void;
}

function updateTile(
  tiles: Array<SpendPointsWaysToBoostTile>,
  index: number,
  patch: Partial<SpendPointsWaysToBoostTile>,
): Array<SpendPointsWaysToBoostTile> {
  return tiles.map((tile, tileIndex) => (tileIndex === index ? { ...tile, ...patch } : tile));
}

interface SpendPointsTileEditorProps {
  tile: SpendPointsWaysToBoostTile;
  index: number;
  tiles: Array<SpendPointsWaysToBoostTile>;
  onChange: (tiles: Array<SpendPointsWaysToBoostTile>) => void;
}

function SpendPointsTileEditor({ tile, index, tiles, onChange }: SpendPointsTileEditorProps) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const borderSoft = alpha(theme.palette.divider, 0.12);
  const title = formatTileTitle(tile);

  return (
    <Paper elevation={0} sx={{ border: `1px solid ${borderSoft}`, borderRadius: 1, overflow: 'hidden' }}>
      <Box
        sx={{
          px: 1.25,
          py: 0.75,
          bgcolor: alpha(theme.palette.primary.main, expanded ? 0.08 : 0.05),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          borderBottom: expanded ? `1px solid ${borderSoft}` : 'none',
        }}
      >
        <Button
          variant="text"
          color="primary"
          onClick={() => setExpanded((value) => !value)}
          endIcon={
            <ExpandMoreIcon
              fontSize="small"
              sx={{
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: theme.transitions.create('transform', { duration: theme.transitions.duration.shortest }),
              }}
            />
          }
          sx={{
            minWidth: 0,
            justifyContent: 'flex-start',
            px: 0.5,
            textTransform: 'none',
            fontWeight: 700,
            color: 'text.primary',
          }}
        >
          <Typography variant="subtitle2" component="span" noWrap>
            {title}
          </Typography>
        </Button>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
          <Chip label={`Position ${index + 1}`} size="small" color="primary" variant="outlined" />
          <Tooltip title="Move tile up">
            <Box component="span">
              <IconButton
                aria-label={`Move tile ${index + 1} up`}
                disabled={index === 0}
                onClick={() => onChange(moveTile(tiles, index, -1))}
              >
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>
          <Tooltip title="Move tile down">
            <Box component="span">
              <IconButton
                aria-label={`Move tile ${index + 1} down`}
                disabled={index === tiles.length - 1}
                onClick={() => onChange(moveTile(tiles, index, 1))}
              >
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>
          <Button
            size="small"
            variant={tile.isActive ? 'contained' : 'outlined'}
            color={tile.isActive ? 'success' : 'inherit'}
            onClick={() => onChange(updateTile(tiles, index, { isActive: !tile.isActive }))}
          >
            {tile.isActive ? 'Active' : 'Inactive'}
          </Button>
          <Tooltip title="Remove tile">
            <IconButton
              aria-label={`Remove tile ${index + 1}`}
              onClick={() => onChange(tiles.filter((_, tileIndex) => tileIndex !== index))}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ p: 1.5 }}>
          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12 }}>
              <PayloadBuilderRegionChipsField
                label="Eligible Regions"
                helperText="Leave empty to show this tile in every region."
                regions={tile.enabledRegions}
                onChange={(enabledRegions) => onChange(updateTile(tiles, index, { enabledRegions }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <PayloadBuilderTextField
                label="Start at"
                type="datetime-local"
                value={tile.startAt}
                onChange={(event) => onChange(updateTile(tiles, index, { startAt: event.target.value }))}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <PayloadBuilderTextField
                label="End at"
                type="datetime-local"
                value={tile.endAt}
                onChange={(event) => onChange(updateTile(tiles, index, { endAt: event.target.value }))}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <PayloadBuilderImageIdField
                label="Image ID"
                required
                value={tile.imageId}
                onChange={(imageId) => onChange(updateTile(tiles, index, { imageId }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <PayloadBuilderTextField
                label="Title"
                value={tile.title}
                onChange={(event) => onChange(updateTile(tiles, index, { title: event.target.value }))}
                required
                {...getPayloadBuilderCopyLengthWarningProps(tile.title, PAYLOAD_BUILDER_TITLE_WARNING_LENGTH)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <PayloadBuilderTextField
                label="CTA label"
                value={tile.ctaLabel}
                onChange={(event) => onChange(updateTile(tiles, index, { ctaLabel: event.target.value }))}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <PayloadBuilderTextField
                label="Description"
                value={tile.description}
                onChange={(event) => onChange(updateTile(tiles, index, { description: event.target.value }))}
                required
                multiline
                minRows={2}
                {...getPayloadBuilderCopyLengthWarningProps(
                  tile.description,
                  PAYLOAD_BUILDER_DESCRIPTION_WARNING_LENGTH,
                )}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <PayloadBuilderTextField
                label="CTA URL"
                value={tile.ctaUrl}
                onChange={(event) => onChange(updateTile(tiles, index, { ctaUrl: event.target.value }))}
                helperText="Use an https:// URL or an internal route starting with /"
                required
              />
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Paper>
  );
}

export default function SpendPointsTilesSection({ tiles, onChange }: SpendPointsTilesSectionProps) {
  return (
    <SectionCard
      title="Ways to Boost tiles"
      subtitle="Tiles shown on the Société Spend Points page."
      count={tiles.length}
    >
      <Stack spacing={2}>
        {tiles.map((tile, index) => (
          <SpendPointsTileEditor key={tile.draftId} tile={tile} index={index} tiles={tiles} onChange={onChange} />
        ))}
        <Button
          variant="outlined"
          onClick={() => onChange([...tiles, createDefaultSpendPointsTile(getNextTileDraftLabel(tiles))])}
        >
          Add tile
        </Button>
      </Stack>
    </SectionCard>
  );
}
